/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { sigintUpdateSensor } from "../funcs/sigintUpdateSensor.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SigintSensor } from "../models/components/sigintsensor.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateSigintSensorRequest,
  UpdateSigintSensorSecurity,
} from "../models/operations/updatesigintsensor.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateSigintSensorMutationVariables = {
  request: UpdateSigintSensorRequest;
  security?: UpdateSigintSensorSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateSigintSensorMutationData = SigintSensor;

export type UpdateSigintSensorMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateSensor sigint
 *
 * @remarks
 * Partially update a sensor. Omitted fields and JSON null preserve stored values. Empty optional text clears it. Omitted signal_ids preserves membership, while [] clears it and a non-empty array replaces and reorders it atomically. Mode changes validate the final membership: exclusive permits at most 255 signals and ordered_score at most 10; incomplete drafts remain valid.
 */
export function useUpdateSigintSensorMutation(
  options?: MutationHookOptions<
    UpdateSigintSensorMutationData,
    UpdateSigintSensorMutationError,
    UpdateSigintSensorMutationVariables
  >,
): UseMutationResult<
  UpdateSigintSensorMutationData,
  UpdateSigintSensorMutationError,
  UpdateSigintSensorMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateSigintSensorMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateSigintSensor(): MutationKey {
  return ["@gram/client", "sigint", "updateSensor"];
}

export function buildUpdateSigintSensorMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateSigintSensorMutationVariables,
  ) => Promise<UpdateSigintSensorMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateSigintSensor(),
    mutationFn: function updateSigintSensorMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateSigintSensorMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(sigintUpdateSensor(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
